import { useState, useEffect } from "react";

import { distinct } from "../../../../../helpers/JSONTransformations";

import { trim } from "../../../../../helpers/StringManipulation";
import {
  checkAllVariantFieldsHaveValues,
  findDuplicateSkuRowIndicies,
  findDuplicateOptionsRowIndicies,
  checkAllVariantPrices
} from "./helpers";

const useVariantsValidation = ({ columns, rows, sidebarIsOpen, minimumPrice }) => {
  const initialState = {
    formHasAnInvalidField: false,
    duplicateSkuRowIndicies: [],
    duplicateOptionsRowIndices: [],
    allVariantFieldsHaveValues: false,
    duplicateTitleIndicies: [],
    allVariantOptionsHaveTitles: false,
    formHasAnInvalidPrice: false
  };

  const [formHasAnInvalidField, setFormHasAnInvalidFields] = useState(
    initialState.formHasAnInvalidField
  );
  const [duplicateSkuRowIndicies, setDuplicateSkuRowIndicies] = useState(
    initialState.duplicateSkuRowIndicies
  );
  const [duplicateOptionsRowIndices, setDuplicateOptionsRowIndices] = useState(
    initialState.duplicateOptionsRowIndices
  );
  const [allVariantFieldsHaveValues, setAllVariantFieldsHaveValues] = useState(
    initialState.allVariantFieldsHaveValues
  );
  const [formHasAnInvalidPrice, setFormHasAnInvalidPrice] = useState(
    initialState.formHasAnInvalidPrice
  );

  const isFieldInvalid = (column, row, rowIndex) => {
    const fieldHasNoValue =
      !(column.stateKey === "stock") && !row[column.stateKey];

    const SKUsAreDuplicated =
      column.stateKey === "sku" &&
      allVariantFieldsHaveValues &&
      rowIndex !== undefined &&
      duplicateSkuRowIndicies.includes(rowIndex);

    const rowOptionsAreDuplicated =
      !duplicateSkuRowIndicies.includes(rowIndex) &&
      allVariantFieldsHaveValues &&
      rowIndex !== undefined &&
      duplicateOptionsRowIndices.includes(rowIndex) &&
      column.stateKey.includes("variantOption");

    const invalidMinimumPrice = column.stateKey === "price" && minimumPrice > row[column.stateKey];

    return fieldHasNoValue || SKUsAreDuplicated || rowOptionsAreDuplicated || invalidMinimumPrice;
  };

  const [duplicateTitleIndicies, setDuplicateTitleIndicies] = useState(
    initialState.duplicateTitleIndicies
  );

  const checkForDuplicateTitleIndices = (_columns) =>
    setDuplicateTitleIndicies(
      distinct(
        _columns.map(
          (column, index) =>
            _columns.filter(
              (otherColumn) => trim(column.title) === trim(otherColumn.title)
            ).length > 1 && index
        )
      )
    );

  const [
    allVariantOptionsHaveTitles,
    setAllVariantOptionsHaveTitles
  ] = useState(initialState.allVariantOptionsHaveTitles);

  useEffect(() => {
    const allVariantOptionsHaveTitles = columns.every((column) => column.title);
    setAllVariantOptionsHaveTitles(allVariantOptionsHaveTitles);

    const allVariantFieldsHaveValues = checkAllVariantFieldsHaveValues(
      rows,
      setAllVariantFieldsHaveValues
    );

    const duplicateSkuRowIndicies = findDuplicateSkuRowIndicies(
      rows,
      setDuplicateSkuRowIndicies
    );

    const duplicateOptionsRowIndicies = findDuplicateOptionsRowIndicies(
      rows,
      setDuplicateOptionsRowIndices
    );

    const formHasAnInvalidPrice = checkAllVariantPrices(
      rows,
      setFormHasAnInvalidPrice,
      minimumPrice
    );

    setFormHasAnInvalidFields(
      !allVariantOptionsHaveTitles ||
        !allVariantFieldsHaveValues ||
        !!duplicateSkuRowIndicies.length ||
        !!duplicateOptionsRowIndicies.length ||
        !!formHasAnInvalidPrice
    );
  }, [columns, rows, sidebarIsOpen]);

  const resetState = () => {
    setFormHasAnInvalidFields(initialState.formHasAnInvalidField);
    setDuplicateSkuRowIndicies(initialState.duplicateSkuRowIndicies);
    setDuplicateOptionsRowIndices(initialState.duplicateOptionsRowIndices);
    setAllVariantFieldsHaveValues(initialState.allVariantFieldsHaveValues);
    setDuplicateTitleIndicies(initialState.duplicateTitleIndicies);
    setAllVariantOptionsHaveTitles(initialState.allVariantOptionsHaveTitles);
    setFormHasAnInvalidPrice(initialState.formHasAnInvalidPrice);
  };

  return {
    formHasAnInvalidField,
    formHasAnInvalidPrice,
    isFieldInvalid,
    duplicateSkuRowIndicies,
    duplicateOptionsRowIndices,
    allVariantOptionsHaveTitles,
    allVariantFieldsHaveValues,
    duplicateTitleIndicies,
    checkForDuplicateTitleIndices,

    resetState
  };
};

export default useVariantsValidation;
